/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from 'react';

import GeocodingService from '@src/services/GeocodingService';

let reverseGeocodeChain = Promise.resolve();

const enqueueReverseGeocode = task => {
  const run = reverseGeocodeChain.then(() => task());
  reverseGeocodeChain = run.catch(() => {});
  return run;
};

const reverseGeocodeInflight = new Map();
const reverseGeocodeKey = (lat, lon) => `${Number(lat).toFixed(6)},${Number(lon).toFixed(6)}`;

const useGeocoding = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [streets, setStreets] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [locations, setLocations] = useState([]);

  const geocodingService = useRef(new GeocodingService()).current;
  const debounceTimers = useRef({});
  const reverseOpsRef = useRef(0);

  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const debounce = (fn, key, delay = 500) => {
    return (...args) => {
      if (debounceTimers.current[key]) {
        clearTimeout(debounceTimers.current[key]);
      }
      debounceTimers.current[key] = setTimeout(() => {
        fn(...args);
      }, delay);
    };
  };

  const searchCountries = useCallback(
    debounce(async query => {
      if (!query || query.trim().length < 2) {
        setCountries([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await geocodingService.getCountries(query);
        setCountries(results);
      } catch (err) {
        setError(err?.message || 'Error searching countries');
        setCountries([]);
      } finally {
        setLoading(false);
      }
    }, 'countries'),
    [geocodingService],
  );

  const searchStates = useCallback(
    debounce(async (country, stateQuery) => {
      if (!country || !stateQuery || stateQuery.trim().length < 2) {
        setStates([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await geocodingService.getStates(country, stateQuery);
        setStates(results);
      } catch (err) {
        setError(err?.message || 'Error searching states');
        setStates([]);
      } finally {
        setLoading(false);
      }
    }, 'states'),
    [geocodingService],
  );

  const searchCities = useCallback(
    debounce(async (country, cityQuery, state = '') => {
      if (!country || !cityQuery || cityQuery.trim().length < 2) {
        setCities([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await geocodingService.getCities(country, cityQuery, state);
        setCities(results);
      } catch (err) {
        setError(err?.message || 'Error searching cities');
        setCities([]);
      } finally {
        setLoading(false);
      }
    }, 'cities'),
    [geocodingService],
  );

  const searchStreets = useCallback(
    debounce(async (country, city, streetQuery) => {
      if (!country || !city || !streetQuery || streetQuery.trim().length < 2) {
        setStreets([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await geocodingService.getStreets(country, city, streetQuery);
        setStreets(results);
      } catch (err) {
        setError(err?.message || 'Error searching streets');
        setStreets([]);
      } finally {
        setLoading(false);
      }
    }, 'streets'),
    [geocodingService],
  );

  const searchAddresses = useCallback(
    debounce(async (country, city, street, addressQuery) => {
      if (!country || !city || !street) {
        setAddresses([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await geocodingService.getAddresses(country, city, street, addressQuery);
        setAddresses(results);
      } catch (err) {
        setError(err?.message || 'Error searching addresses');
        setAddresses([]);
      } finally {
        setLoading(false);
      }
    }, 'addresses'),
    [geocodingService],
  );

  const searchLocations = useCallback(
    debounce(async query => {
      if (!query || query.trim().length < 3) {
        setLocations([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await geocodingService.searchLocations(query);
        setLocations(results);
      } catch (err) {
        setError(err?.message || 'Error searching locations');
        setLocations([]);
      } finally {
        setLoading(false);
      }
    }, 'locations'),
    [geocodingService],
  );

  const getCoordinates = useCallback(
    async addressString => {
      setLoading(true);
      setError(null);

      try {
        const coords = await geocodingService.getCoordinatesFromAddress(addressString);
        return coords;
      } catch (err) {
        setError(err?.message || 'Error getting coordinates');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [geocodingService],
  );

  const getAddressFromCoords = useCallback(
    async (lat, lon) => {
      const key = reverseGeocodeKey(lat, lon);
      let shared = reverseGeocodeInflight.get(key);
      if (!shared) {
        shared = enqueueReverseGeocode(() =>
          geocodingService.getAddressFromCoordinates(lat, lon),
        ).finally(() => {
          reverseGeocodeInflight.delete(key);
        });
        reverseGeocodeInflight.set(key, shared);
      }

      reverseOpsRef.current += 1;
      if (reverseOpsRef.current === 1) {
        setLoading(true);
        setError(null);
      }

      try {
        return await shared;
      } catch (err) {
        setError(err?.message || 'Error getting address from coordinates');
        return null;
      } finally {
        reverseOpsRef.current -= 1;
        if (reverseOpsRef.current === 0) {
          setLoading(false);
        }
      }
    },
    [geocodingService],
  );

  const searchStructured = useCallback(
    async addressParts => {
      setLoading(true);
      setError(null);

      try {
        const results = await geocodingService.searchStructured(addressParts);
        return results;
      } catch (err) {
        setError(err?.message || 'Error searching structured address');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [geocodingService],
  );

  const clearCountries = useCallback(() => {
    setCountries([]);
  }, []);

  const clearStates = useCallback(() => {
    setStates([]);
  }, []);

  const clearCities = useCallback(() => {
    setCities([]);
  }, []);

  const clearStreets = useCallback(() => {
    setStreets([]);
  }, []);

  const clearAddresses = useCallback(() => {
    setAddresses([]);
  }, []);

  const clearLocations = useCallback(() => {
    setLocations([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    countries,
    states,
    cities,
    streets,
    addresses,
    locations,
    searchCountries,
    searchStates,
    searchCities,
    searchStreets,
    searchAddresses,
    searchLocations,
    getCoordinates,
    getAddressFromCoords,
    searchStructured,
    clearCountries,
    clearStates,
    clearCities,
    clearStreets,
    clearAddresses,
    clearLocations,
    clearError,
  };
};

export default useGeocoding;
